import { MS_PER_DAY, toUtcDateKey, utcDayMs } from '@/utils/date';
import type { ProgramInput } from './mm-program-schema';

/*
 * Which UTC days a date input may offer, so the editors only let people pick a schedule or window that is well formed
 * and what a filter can match. Days are `YYYY-MM-DD`; bounds are inclusive. Past days stay open: settings are
 * corrected for a backfill. bop-api still validates on save: these only stop a dead end from being picked.
 */

export interface DayBounds {
  /** First day that may be picked */
  min?: string;
  /** Last day that may be picked */
  max?: string;
  /** Nothing may be picked; `reason` says why */
  disabled?: boolean;
  reason?: string;
}

export const NO_BOUNDS: DayBounds = {};

export const todayUtc = (now = new Date()) => toUtcDateKey(now);

export const addDays = (day: string, days: number) => toUtcDateKey(utcDayMs(day) + days * MS_PER_DAY);

const latest = (...days: (string | undefined)[]) => days.filter(Boolean).sort().at(-1);

export const isWithinBounds = (day: string, bounds: DayBounds) => !bounds.disabled && (!bounds.min || day >= bounds.min) && (!bounds.max || day <= bounds.max);

/** The day if it may be picked, else blank: a value left over from before the bounds changed stops applying. */
export const withinBoundsOrBlank = (day: string, bounds: DayBounds) => (day && isWithinBounds(day, bounds) ? day : '');

const dayOf = (value: string) => value.trim().slice(0, 10);

/** A schedule's first day comes before its exclusive end. */
export const scheduleStartBounds = (program: Pick<ProgramInput, 'end_date'>): DayBounds => {
  const end = dayOf(program.end_date);
  return end ? { max: addDays(end, -1), reason: 'A schedule starts before its end.' } : NO_BOUNDS;
};

/** A schedule ends after its first day. */
export const scheduleEndBounds = (program: Pick<ProgramInput, 'start_date'>): DayBounds => {
  const start = dayOf(program.start_date);
  return start ? { min: addDays(start, 1), reason: 'A schedule ends after it starts.' } : NO_BOUNDS;
};

/** Starting a new schedule ends the open ones on its start, so that day obeys their end bounds too. */
export const nextScheduleStartBounds = (programs: Pick<ProgramInput, 'start_date' | 'end_date'>[]): DayBounds => {
  const openStarts = programs.filter((program) => !program.end_date.trim()).map((program) => dayOf(program.start_date));
  const latestStart = latest(...openStarts);
  return latestStart ? { min: addDays(latestStart, 1), reason: 'The open schedule ends after it starts.' } : NO_BOUNDS;
};

export type ScheduleStatusFilter = 'inForce' | 'upcoming' | 'ended';

/**
 * The days a period filter can match for the statuses shown. In force alone means today, so a period adds nothing;
 * upcoming schedules are in force from tomorrow at the earliest, and ended ones until yesterday at the latest.
 */
export const periodBounds = (statuses: ScheduleStatusFilter[], today = todayUtc()): DayBounds => {
  const only = statuses.length === 1 ? statuses[0] : null;
  if (only === 'inForce') return { disabled: true, reason: 'In force means today, so a period does not apply. Tick Upcoming or Ended to filter by period.' };
  if (only === 'upcoming') return { min: addDays(today, 1), reason: 'Upcoming schedules start after today.' };
  if (only === 'ended') return { max: addDays(today, -1), reason: 'Ended schedules were last in force before today.' };
  return NO_BOUNDS;
};

export interface Period {
  /** First UTC day, inclusive; blank is open */
  from: string;
  /** Last UTC day, inclusive; blank is open */
  to: string;
}

/** The part of a period the bounds allow: a side outside them stops applying, and a disabled period applies nowhere. */
export const boundedPeriod = (period: Period, bounds: DayBounds): Period => ({ from: withinBoundsOrBlank(period.from, bounds), to: withinBoundsOrBlank(period.to, bounds) });

/** Each side of a period stays on its own side of the other. */
export const periodSideBounds = (period: Period, bounds: DayBounds): { from: DayBounds; to: DayBounds } => ({
  from: { ...bounds, max: [bounds.max, period.to].filter(Boolean).sort()[0] },
  to: { ...bounds, min: latest(bounds.min, period.from) },
});

/** A one-off window ends on or after the day it starts. */
export const oneOffEndBounds = (start: string): DayBounds => (dayOf(start) ? { min: dayOf(start), reason: 'Downtime ends after it starts.' } : NO_BOUNDS);
